
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import React, { useState } from 'react';
import Image from 'next/image';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import Icon, { AnimatedIcon } from 'shared/library/atoms/Icon';
import { H1, H2, H4, H6 } from 'library/atoms/Typography';
import Input from 'library/atoms/Input';
import Button from 'library/atoms/Button';
import { Formik, Form, FormikHelpers } from 'formik';
import { Text } from 'library/atoms/Typography';
import * as Yup from 'yup';
import EolaLayout from 'library/atoms/EolaLayout';
import flag from 'assets/icons/iconsWithGradient/flag.svg';
import rocket from 'assets/icons/iconsWithGradient/rocket.svg';
import tick from 'assets/icons/iconsWithGradient/tick.svg';
import growth from 'assets/icons/iconsWithGradient/growth.svg';
import Carousel, { CarouselSlide } from 'library/molecules/Carousel';
import { EolaMember, FoundersData, TeamMemberData } from 'enums/teamMemberData';
import translateEolaMember from 'helpers/translateEolaMember';
import { rgba } from 'polished';
import HeadContainer from 'library/atoms/HeadContainer';
import { generateCloudinaryUrl } from 'shared/helpers/generateCloudinaryUrl';
import { LEGACY_BUSINESS_ROUTES } from 'enums/routes';
interface TeamMemberCardInterface {
    staffMemberImageUrl: string;
    backgroundUrl: string;
    name: string;
    role: string;
    origin: string;
    favouriteActivity: string;
}
interface ContactForm {
    name: string;
    company: string;
    email: string;
}
export const CONTACT_FORM_INITIAL_VALUES: ContactForm = {
    name: '',
    company: '',
    email: ''
};
export const RequestDemoValidateSchema = Yup.object().shape({
    name: Yup.string()
        .typeError('Please enter a valid name')
        .required('Required'),
    email: Yup.string().email().required('Required')
});
const CARD_WIDTH = 240;
const ICON_SETTINGS = {
    height: 55,
    width: 45,
    layout: 'fixed' as const
};
const MILESTONES = [
    {
        src: flag as string,
        alt: 'flag',
        title: '2017',
        subtitle: 'founded'
    },
    {
        src: rocket as string,
        alt: 'rocket',
        title: '2019',
        subtitle: 'launched'
    },
    {
        src: tick as string,
        alt: 'tick',
        title: '40,000+',
        subtitle: 'year-one-customers'
    },
    {
        src: growth as string,
        alt: 'growth-graph',
        title: '100+',
        subtitle: 'businesses-live'
    }
];
const About = () => {
    const { t } = useTranslation('about');
    const [formSubmitSuccess, setFormSubmitSuccess] = useState(false);
    const { push } = useRouter();
    const onSubmit = async (values: ContactForm, { resetForm }: FormikHelpers<ContactForm>) => {
        const settings = {
            method: 'POST',
            body: JSON.stringify({
                page: 'About Us',
                email: values.email,
                name: values.name,
                company: values.company
            }),
            headers: {
            //needs to be empty for this to work - https://community.zapier.com/code-webhooks-52/zapier-webhooks-blocked-by-cors-policy-8259
            }
        };
        {
            const fetchResponse = await fetch(`https://hooks.zapier.com/hooks/catch/4546994/ob825v3/`, settings);
            const data = await fetchResponse.json();
            if (data.status != 'success') {
                return;
            }
            resetForm({});
            setFormSubmitSuccess(true);
            // URL change for GTM to track
            push({
                pathname: '/about-us',
                query: '/join?requested=true#demo_request_form'
            }, undefined, { shallow: true });
        }
    };
    return (<>
      <HeadContainer title={t('common:about-us')} description={t('description')} keywords={t('keywords')} image={generateCloudinaryUrl('image/upload/v1629451524/eola/public_images/about-us-preview-image.png')}/>
      <EolaLayout>
        <StyledHero>
          <H1>{t('common:about-us')}</H1>
        </StyledHero>
        <StyledSection>
          <StyledSectionTextContent>
            <Icon size={40} name="running-man" color="grey"/>
            <H2 color="secondary">{t('what-we-do')}</H2>
            <Text color="secondary" fontStyle="p1">
              {t('what-we-do-para1')}
            </Text>
            <Text color="secondary" fontStyle="p1">
              {t('what-we-do-para2')}
            </Text>
          </StyledSectionTextContent>
        </StyledSection>
        <StyledBlueSectionContainer>
          <StyledSectionTextContent>
            <Icon size={40} name="cog" color="white"/>
            <H2> {t('eola-unique')}</H2>
            <Text fontStyle="p1">{t('eola-unique-para1')}</Text>
            <Text fontStyle="p1">{t('eola-unique-para2')}</Text>
            <Text fontStyle="p1">{t('eola-unique-para3')}</Text>
          </StyledSectionTextContent>
        </StyledBlueSectionContainer>
        <StyledSection variant="grey">
          <StyledMilestonesContainer>
            {MILESTONES.map(milestone => (<StyledMilestone key={milestone.title}>
                <Image {...ICON_SETTINGS} src={milestone.src} alt={t(milestone.alt)}/>
                <H4>{milestone.title}</H4>
                <H6 color="secondary">{t(milestone.subtitle)}</H6>
              </StyledMilestone>))}
          </StyledMilestonesContainer>
        </StyledSection>
        <StyledFoundersContainer>
          <StyledFounderColumn>
            {FoundersData.map(founder => (<StyledCardWrapper key={founder.name}>
                <CardMember {...founder}/>
              </StyledCardWrapper>))}
          </StyledFounderColumn>
          <StyledFounderColumn>
            <StyledFoundersText>
              <Icon size={40} name="crown" color="grey"/>
              <H2 color="secondary"> {t('the-founders')}</H2>
              <Text color="secondary" fontStyle="p1">
                {t('the-founders-para1')}
              </Text>
            </StyledFoundersText>
          </StyledFounderColumn>
        </StyledFoundersContainer>
        <StyledSection variant="grey">
          <StyledSectionTextContent>
            <Icon size={40} name="multiple-users" color="grey"/>
            <H2 color="secondary">{t('the-team')}</H2>
          </StyledSectionTextContent>
        </StyledSection>
        <StyledSection variant="gradient">
          <StyledCarousel>
            {TeamMemberData.map(person => (<CarouselSlide key={person.name}>
                <CardMember {...person}/>
              </CarouselSlide>))}
          </StyledCarousel>
          <StyledStoryBox>
            <Icon size={40} name="script" color="grey"/>
            <H2>{t('our-story')}</H2>
            <Text fontStyle="p1">{t('our-story-para1')}</Text>
            <Text fontStyle="p1">{t('our-story-para2')}</Text>
            <Text fontStyle="p1">{t('our-story-para3')}</Text>
            <Text fontStyle="p1">{t('our-story-para4')}</Text>
            <Text fontStyle="p1">{t('grow-together')}</Text>
            <Button as="a" variant="eola-primary" href={LEGACY_BUSINESS_ROUTES.JOIN.url()}>
              {t('get-started')}
            </Button>
          </StyledStoryBox>
        </StyledSection>
        <StyledSection variant="primary">
          <StyledSectionTextContent>
            <Icon size={40} name="speech-bubble" color="currentColor"/>
            <H2 mt={3}>{t('have-a-chat')}</H2>
            <Text fontStyle="p1">{t('request-demo-para1')}</Text>
          </StyledSectionTextContent>
          {!formSubmitSuccess ? (<Formik initialValues={CONTACT_FORM_INITIAL_VALUES} onSubmit={onSubmit} validationSchema={RequestDemoValidateSchema}>
              {({ handleSubmit }) => (<StyledForm>
                  <Input label={t('your-name')} placeholder={t('your-name')} name="name"/>
                  <Input label={t('your-company')} placeholder={t('your-company')} name="company"/>
                  <Input label={t('your-email')} placeholder="joanadoe@gmail.com" name="email"/>
                  {!formSubmitSuccess && (<Button variant="eola-primary" type="submit" onClick={handleSubmit} mt={4}>
                      {t('request-demo')}
                    </Button>)}
                </StyledForm>)}
            </Formik>) : (<StyledFormSuccessMessage>
              <StyledAnimatedIconSection>
                <StyleAnimatedIconContainer>
                  <AnimatedIcon name="clap" size={52} autoplay m="auto"/>
                </StyleAnimatedIconContainer>
              </StyledAnimatedIconSection>

              <H2>{t('all-done')}</H2>
              <Text fontStyle="p3">{t('in-touch')}</Text>
              <Button variant="eola-primary" success={formSubmitSuccess}>
                {t('request-demo')}
              </Button>
            </StyledFormSuccessMessage>)}
        </StyledSection>
      </EolaLayout>
    </>);
};
export default About;
const CardMember = (person: EolaMember) => {
    const { t } = useTranslation('about');
    return (<StyledTeamMemberCardContainer>
      <StyledTeamMemberImage staffMemberImageUrl={generateCloudinaryUrl(person.profileImageUrl, 'profile')}/>
      <StyledTeamMemberCardBody backgroundUrl={generateCloudinaryUrl(person.backgroundUrl)}>
        <StyledTeamMemberCardTopContainer>
          <H4>{person.name}</H4>
          <Text fontStyle="p2">{translateEolaMember(person, t).role}</Text>
        </StyledTeamMemberCardTopContainer>
        <StyledTeamMemberCardBottomContainer>
          <StyledTeamMemberCardBottomItem>
            <Icon size={20} name="pin" color="white"/>
            <Text fontStyle="p3">{person.origin}</Text>
          </StyledTeamMemberCardBottomItem>
          <StyledTeamMemberCardBottomItem>
            <Icon size={20} name="heart" color="white"/>
            <Text fontStyle="p3">
              {translateEolaMember(person, t).favouriteActivity}
            </Text>
          </StyledTeamMemberCardBottomItem>
        </StyledTeamMemberCardBottomContainer>
      </StyledTeamMemberCardBody>
    </StyledTeamMemberCardContainer>);
};
// TODO - move to Eola variables if used more regularly
const customColor = {
    pink: '#F9A9BD',
    lightPink: '#FDB1C4',
    lightBlue: '#BACCE5',
    purple: '#3E48A0',
    errorState: '#FFB932'
};
const StyledHero = styled.main(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundImage: `url("https://res.cloudinary.com/eola/image/upload/f_auto,q_auto:eco/v1628241251/eola/public_images/about-us-hero.png")`,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    height: 170,
    width: '100%',
    color: theme.colors.white,
    [theme.mediaQueries.sm]: {
        height: 250,
        backgroundPosition: 'center'
    },
    [theme.mediaQueries.md]: {
        height: 300,
        backgroundPosition: 'center top',
        borderRadius: theme.radii[3]
    },
    [theme.mediaQueries.xxxl]: {
        height: 520,
        backgroundPosition: 'center top'
    }
}));
const StyledSection = styled.section<{
    variant?: 'primary' | 'grey' | 'gradient';
}>(({ theme }) => ({
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    [theme.mediaQueries.md]: {
        borderRadius: theme.radii[3]
    }
}), ({ theme, variant }) => {
    if (!variant)
        return {};
    const variants = {
        primary: {
            color: theme.colors.white,
            background: theme.colors.primary
        },
        grey: {
            background: theme.colors.background.primary
        },
        gradient: {
            background: `linear-gradient(62.67deg, ${customColor.pink} 3.22%, ${customColor.lightPink} 21.37%, ${customColor.lightBlue} 100%)`,
            backgroundSize: 'cover'
        }
    };
    return variants[variant];
});
const StyledSectionTextContent = styled.div(({ theme }) => ({
    ...theme.mixins.centeredContainer('sm'),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.space[5],
    textAlign: 'center',
    alignItems: 'center',
    padding: theme.mixins.pxSpread([theme.space[9], 0])
}));
const StyledBlueSectionContainer = styled(StyledSection)(({ theme }) => ({
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'normal',
    justifyContent: 'space-between',
    gap: theme.space[8],
    width: '100%',
    textAlign: 'center',
    backgroundColor: theme.colors.primary,
    color: theme.colors.white,
    //Images
    '&::before, &::after': {
        content: '""',
        display: 'block',
        height: 0,
        width: '100%',
        paddingBottom: '60%',
        backgroundSize: 'cover'
    },
    '&::before': {
        backgroundImage: `url("https://res.cloudinary.com/eola/image/upload/f_auto,q_auto:eco/c_fill,h_1200,w_1200/v1629193156/eola/public_images/about-us-eola-unique-image1.jpg")`,
        backgroundPosition: 'center '
    },
    '&::after': {
        backgroundImage: `url("https://res.cloudinary.com/eola/image/upload/f_auto,q_auto:eco/c_fill,h_1200,w_1200/v1629193114/eola/public_images/about-us-eola-unique-image2.png")`,
        backgroundPosition: 'center -15vw'
    },
    [theme.mediaQueries.xl]: {
        textAlign: 'left',
        //Images
        '&::before, &::after': {
            paddingBottom: 0,
            height: 'auto',
            maxWidth: theme.contentMaxWidth.sm,
            width: 'auto',
            flex: '1 0 0'
        },
        '&::after': {
            backgroundPosition: 'center'
        }
    }
}));
const StyledFoundersContainer = styled(StyledSection)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column-reverse',
    position: 'relative',
    [theme.mediaQueries.lg]: {
        flexDirection: 'row',
        justifyContent: 'center',
        marginLeft: '0'
    }
}));
const StyledFounderColumn = styled.div(({ theme }) => ({
    position: 'relative',
    zIndex: 1,
    display: 'block',
    flex: 1,
    width: '100%',
    maxWidth: theme.contentMaxWidth.xl,
    '&:first-of-type': {
        justifyContent: 'center',
        borderRadius: theme.radii[3],
        background: `linear-gradient(62.67deg,  ${customColor.pink} 3.22%, ${customColor.lightPink} 21.37%, ${customColor.lightBlue} 100%)`
    },
    [theme.mediaQueries.md]: {
        display: 'flex',
        '&:last-of-type': {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between'
        }
    },
    [theme.mediaQueries.lg]: {
        display: 'flex',
        '&:first-of-type': {
            flexDirection: 'row'
        }
    }
}));
const StyledCardWrapper = styled.div(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.space[9],
    paddingBottom: theme.space[9],
    '&:first-of-type': {
        paddingBottom: 0
    },
    [theme.mediaQueries.md]: {
        '&:first-of-type': {
            marginRight: theme.space[5]
        },
        '&:last-of-type': {
            marginLeft: theme.space[5],
            paddingRight: theme.space[5]
        }
    },
    [theme.mediaQueries.lg]: {
        '&:first-of-type': {
            justifyContent: 'flex-end',
            width: '50%',
            marginRight: theme.space[5],
            paddingBottom: theme.space[9]
        },
        '&:last-of-type': {
            justifyContent: 'flex-start',
            width: '50%',
            background: theme.colors.white,
            paddingLeft: theme.space[5]
        }
    }
}));
const StyledFoundersText = styled.div(({ theme }) => ({
    width: '100%',
    textAlign: 'center',
    background: theme.colors.white,
    padding: theme.mixins.pxSpread([theme.space[9], theme.space[5]]),
    p: {
        maxWidth: theme.contentMaxWidth.sm,
        margin: 'auto',
        width: '100%'
    },
    [theme.mediaQueries.lg]: {
        ...theme.mixins.centeredContainer('xs'),
        textAlign: 'right',
        paddingRight: 0,
        paddingLeft: 0,
        p: {
            maxWidth: theme.contentMaxWidth.md
        }
    }
}));
const StyledForm = styled(Form)(({ theme }) => ({
    ...theme.mixins.centeredContainer('md'),
    display: 'block',
    marginBottom: theme.space[8],
    padding: theme.space[9],
    boxShadow: theme.shadows.black[1],
    border: 'none',
    borderRadius: theme.radii[3],
    label: {
        textAlign: 'left',
        marginTop: theme.space[2]
    },
    input: {
        color: theme.colors.text.primary,
        marginTop: theme.space[2],
        marginBottom: theme.space[2],
        '&::-webkit-input-placeholder': {
            color: theme.colors.state.disabled
        }
    },
    p: {
        textAlign: 'left',
        color: `${customColor.errorState}`
    }
}));
const StyledFormSuccessMessage = styled.div(({ theme }) => ({
    ...theme.mixins.centeredContainer('md'),
    paddingTop: theme.space[9],
    paddingBottom: theme.space[9],
    textAlign: 'center',
    alignItems: 'center',
    boxShadow: theme.shadows.black[1],
    '> *': {
        marginBottom: theme.space[5]
    }
}));
const StyledMilestonesContainer = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.space[8],
    padding: theme.mixins.pxSpread([theme.space[9], 0]),
    background: theme.colors.background.primary,
    ...theme.mixins.centeredContainer('lg'),
    [theme.mediaQueries.md]: {
        flexDirection: 'row',
        justifyContent: 'space-between'
    }
}));
const StyledMilestone = styled.div(({ theme }) => ({
    minWidth: 150,
    width: '100%',
    textAlign: 'center',
    alignItems: 'center',
    fontWeight: theme.fontWeights['heading'],
    h4: {
        marginTop: theme.space[5]
    }
}));
const StyledCarousel = styled(Carousel)(({ theme }) => ({
    // Create space for the shadow that gets cut off because of the overflow hidden
    ...theme.mixins.createPseudoSpace({
        position: 'bottom',
        size: 9
    }),
    // Grey background to match section above
    '&::before': {
        position: 'absolute',
        top: 0,
        left: 0,
        height: '50%',
        width: '100%',
        content: '""',
        display: 'block',
        margin: 0,
        background: theme.colors.white
    },
    '.swiper-slide': {
        width: CARD_WIDTH
    }
}));
const StyledStoryBox = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.space[5],
    textAlign: 'center',
    padding: theme.space[9],
    margin: theme.mixins.pxSpread([theme.space[8], 0]),
    width: `calc(100% - ${theme.space[8]}px)`,
    maxWidth: theme.contentMaxWidth.md,
    background: theme.colors.white,
    borderRadius: theme.radii[3],
    color: theme.colors.text.primary,
    a: {
        marginRight: 'auto',
        marginLeft: 'auto'
    },
    [theme.mediaQueries.sm]: {
        alignItems: 'flex-start',
        textAlign: 'left',
        width: '100%',
        a: {
            alignSelf: 'center'
        }
    }
}));
const StyledTeamMemberCardContainer = styled.div(() => ({
    position: 'relative' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'flex-end',
    height: 380,
    width: CARD_WIDTH,
    marginTop: 40 // Used 40 pixels to create space for Team Member Headshot
}));
const StyledTeamMemberCardBody = styled.div<Pick<TeamMemberCardInterface, 'backgroundUrl'>>(({ theme, backgroundUrl }) => ({
    display: 'flex',
    flex: '1 0 0 ',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    backgroundImage: `linear-gradient(180deg, ${rgba(customColor.purple, 0)}, ${customColor.purple}), url(${backgroundUrl})`,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    paddingRight: theme.space[5],
    paddingBottom: theme.space[5],
    paddingLeft: theme.space[5],
    textAlign: 'left',
    color: theme.colors.white,
    borderRadius: theme.radii[3],
    boxShadow: theme.shadows.primary[2]
}));
const StyledTeamMemberImage = styled.div<Pick<TeamMemberCardInterface, 'staffMemberImageUrl'>>(({ theme, staffMemberImageUrl }) => ({
    background: `url(${staffMemberImageUrl}) no-repeat center / cover`,
    height: 80,
    width: 80,
    marginBottom: -theme.space[9],
    marginLeft: theme.space[5],
    position: 'absolute',
    top: -40,
    borderRadius: '50%',
    boxShadow: theme.shadows.primary[2]
}));
const StyledTeamMemberCardTopContainer = styled.div(({ theme }) => ({
    borderBottom: `1px solid ${rgba(theme.colors.white, 0.5)}`,
    paddingBottom: theme.space[5]
}));
const StyledTeamMemberCardBottomContainer = styled.div(({ theme }) => ({
    marginTop: theme.space[2],
    p: {
        paddingLeft: theme.space[2]
    }
}));
const StyledTeamMemberCardBottomItem = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    paddingTop: theme.space[2],
    paddingBottom: theme.space[2],
    alignItems: 'center',
    p: {
        paddingLeft: theme.space[2]
    }
}));
const StyleAnimatedIconContainer = styled.div(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    background: theme.colors.white,
    width: theme.space[10],
    height: theme.space[10],
    borderRadius: '50%'
}));
const StyledAnimatedIconSection = styled.div(() => ({
    display: 'flex',
    justifyContent: 'center'
}));

    async function __Next_Translate__getStaticProps__1961a27c763__(ctx) {
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/about-us/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1961a27c763__ as getStaticProps }
  